import { TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { InputType } from 'config'
import { useSharedLoan } from 'context/SharedLoanContext'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { downloadFile, downloadReinstatement, getReinstatementLineItems, getServicingNotes } from 'services'
import { Button } from 'stories/components'
import { InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { defaultInputs, TOTAL_TITLE } from './constants'

// Line item as returned by the backend.
interface BackendLine {
  title: string
  value: string
  kind?: 'computed' | 'manual'
  optional?: boolean
}

// Line item as shown/edited on the page.
interface LineItem {
  title: string
  value: string
  /** Optional lines (the trust balance credit) can be toggled off before generating. */
  optional?: boolean
  /** Whether an optional line is currently included in the total/PDF. */
  included: boolean
  /** A line added from the category picker can be removed back into the picker. */
  isCategory?: boolean
}

// What the backend reports about the delinquency behind the quote.
interface Summary {
  paymentsPastDue: number
  regularPayment: number
  firstPastDueDate: string
  lastPastDueDate: string
  interestPaidDate: string
  currentUpb: number
  /** Set when the loan has matured by the good-through date; the whole balance is then due. */
  maturedOn?: string
  /** The maturity date the quote was built on, which may be the override entered above. */
  maturityDate?: string
  /** The maturity date actually on the loan, so an override can be called out. */
  loanMaturityDate?: string
}

const toNumber = (value: any) => {
  const n = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : Number(value)
  return isNaN(n) ? 0 : n
}

// Blank values render as $0 on the statement.
const displayValue = (value: string) => (value === '' || value == null ? '0' : value)

// Format a number as US currency for display, preserving a leading minus for credits.
const formatUSD = (n: number) =>
  `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export default function Reinstatement() {
  const shared = useSharedLoan()
  const isShared = !!shared?.readOnly
  const [loading, setLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, InputType>>(defaultInputs())
  const [servicingInfo, setServicingInfo] = useState<any>({})
  const [lineItems, setLineItems] = useState<LineItem[]>([])
  const [available, setAvailable] = useState<{ title: string; value: string }[]>([])
  const [summary, setSummary] = useState<Summary | null>(null)

  const reinstatementDate = inputs.reinstatementDate?.value
  const maturityDate = inputs.maturityDate?.value
  const sendEmail = inputs.sendEmail?.value === 'email'

  useEffect(() => {
    if (isShared) return
    const init = async () => {
      setLoading(true)
      const { data: servicingSummaryInfo } = await getServicingNotes()
      setServicingInfo(servicingSummaryInfo)
      // Seed the maturity date with the loan's own, so the quote starts where the loan stands and
      // the servicer only has to touch the field to quote an extension.
      const loanMaturity = servicingSummaryInfo?.curMatDate || servicingSummaryInfo?.originalMatDate || ''
      if (loanMaturity) {
        setInputs((prev) => {
          if (prev.maturityDate?.value) return prev
          const next = cloneDeep(prev)
          next.maturityDate.value = loanMaturity
          return next
        })
      }
      setLoading(false)
    }
    init()
  }, [isShared])

  // Recompute the reinstatement line items whenever the Good through Date or the maturity date the
  // quote is being run against changes.
  useEffect(() => {
    if (isShared) return
    const loadLineItems = async () => {
      if (!reinstatementDate) {
        setLineItems([])
        setAvailable([])
        setSummary(null)
        return
      }

      setLoading(true)
      try {
        const res = await getReinstatementLineItems({ reinstatementDate, maturityDate })
        const raw: BackendLine[] = res.lines || []
        const lastIdx = raw.length - 1

        const shown: LineItem[] = []
        const addable: { title: string; value: string }[] = []

        raw.forEach((item, index) => {
          if (index === lastIdx) return // total line is derived below

          if (item.kind === 'computed') {
            // Show computed lines that carry a value; anything that computes to $0 is hidden and
            // also kept out of the "add categories" picker.
            if (toNumber(item.value) !== 0) {
              shown.push({
                title: item.title,
                value: displayValue(item.value),
                optional: item.optional,
                // Optional lines are opt-in so they don't silently move the quote.
                included: !item.optional,
              })
            }
          } else {
            // Manual lines have no computed value. Offer them in the picker to add and fill in.
            addable.push({ title: item.title, value: '0' })
          }
        })

        setLineItems(shown)
        setAvailable(addable)
        setSummary(res.summary || null)
      } catch (error) {
        console.error('Failed to load line items:', error)
      } finally {
        setLoading(false)
      }
    }
    loadLineItems()
  }, [reinstatementDate, maturityDate, isShared])

  if (isShared) {
    return (
      <div className="TrustLedger-container">
        <div className="max-w-screen-2xl m-auto">
          <h2 className="text-2xl font-bold flex items-center mb-5">Reinstatement</h2>
          <p className="text-sm text-gray-500 mb-4">
            Reinstatement quotes are not available in the shared read-only view.
          </p>
        </div>
      </div>
    )
  }

  const onChange = (key: string, value: any) => {
    const newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].value = value
    newInputs[key].error = InputValidate(newInputs[key])
    setInputs(newInputs)
  }

  const total = lineItems.reduce((sum, item) => (item.included ? sum + toNumber(item.value) : sum), 0)

  const onLineItemChange = (index: number, value: any) => {
    setLineItems((prev) => prev.map((item, i) => (i === index ? { ...item, value } : item)))
  }

  const onLineItemTitleChange = (index: number, title: string) => {
    setLineItems((prev) => prev.map((item, i) => (i === index ? { ...item, title } : item)))
  }

  const toggleIncluded = (index: number) => {
    setLineItems((prev) => prev.map((item, i) => (i === index ? { ...item, included: !item.included } : item)))
  }

  const removeLineItem = (index: number) => {
    const item = lineItems[index]
    setLineItems((prev) => prev.filter((_, i) => i !== index))
    // A predefined category returns to the picker so it can be added again.
    if (item?.isCategory && item.title) {
      setAvailable((prev) =>
        prev.some((c) => c.title === item.title) ? prev : [...prev, { title: item.title, value: '0' }],
      )
    }
  }

  const addCategory = (title: string) => {
    const category = available.find((c) => c.title === title)
    if (!category) return
    setAvailable((prev) => prev.filter((c) => c.title !== title))
    setLineItems((prev) => [
      ...prev,
      { title: category.title, value: displayValue(category.value), included: true, isCategory: true },
    ])
  }

  const addCustomLine = () => {
    setLineItems((prev) => [...prev, { title: 'New Line Item', value: '0', included: true, isCategory: false }])
  }

  const onGenerate = async () => {
    if (!servicingInfo?.currentInterestRate) {
      toast('Current Interest Rate should be filled', { type: 'error' })
      return
    }
    if (!reinstatementDate) {
      toast('Good through Date should be filled', { type: 'error' })
      return
    }

    let hasError = false
    const newInputs = cloneDeep(inputs)
    const data: Record<string, any> = { reinstatementDate }

    for (const key in newInputs) {
      // Not emailing means no recipient: an address typed before the Action was switched back to
      // Download PDF is not an instruction to send the quote.
      if (key === 'emailTo' && !sendEmail) {
        data[key] = ''
        continue
      }

      newInputs[key].error = InputValidate(newInputs[key])
      data[key] = newInputs[key].value
      if (newInputs[key].error) hasError = true
    }

    if (sendEmail && !data.emailTo) {
      newInputs.emailTo.error = 'Email is required when sending email'
      hasError = true
    }

    setInputs(newInputs)
    if (hasError) return

    // Only included lines go on the statement; blanks become $0. The last line is the total
    // (the backend re-sums and formats it).
    const lines: [string, string][] = lineItems
      .filter((item) => item.included)
      .map((item) => [item.title || '', displayValue(item.value)])
    lines.push([TOTAL_TITLE, total.toString()])
    data.lines = lines

    setLoading(true)
    const pdfData = await downloadReinstatement(data)

    if (!sendEmail) downloadFile(`Reinstatement Quote.pdf`, pdfData)

    setLoading(false)
  }

  return (
    <div className="TrustLedger-container">
      <div className="max-w-screen-2xl m-auto">
        <div className="relative bg-white">
          <h2 className="text-2xl font-bold flex items-center mb-5">Reinstatement</h2>
          <p className="text-sm text-gray-600 -mt-3 mb-4">
            What the borrower must pay to bring this loan current. On a loan that has not yet matured, reinstating does
            not pay it off: the unpaid principal balance stays outstanding.
          </p>
          <LayoutLoading show={loading} />
          <div className="grid gap-4 md:grid-cols-4 grid-cols-1 mb-4 pb-4 border-b border-gray-200">
            {Object.keys(inputs).map((key) => {
              const input = inputs[key]
              // Hide the email field until the action is set to email.
              if (key === 'emailTo' && !sendEmail) return null
              return (
                <div className="input" key={key}>
                  <RenderInput input={input} Key={key} onChange={onChange} />
                </div>
              )
            })}
          </div>

          {reinstatementDate && summary && (
            <div className="grid gap-4 md:grid-cols-4 grid-cols-1 mb-4">
              {[
                ['Payments Past Due', String(summary.paymentsPastDue)],
                ['Regular Payment', formatUSD(summary.regularPayment)],
                ['Interest Paid to Date', summary.interestPaidDate || '-'],
                ['Current UPB', formatUSD(summary.currentUpb)],
              ].map(([label, value]) => (
                <div key={label} className="border rounded-lg p-3 bg-gray-50">
                  <div className="text-sm text-gray-600">{label}</div>
                  <div className="text-lg font-semibold">{value}</div>
                </div>
              ))}
            </div>
          )}

          {reinstatementDate && summary?.maturedOn && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 mb-4">
              This loan matured on {summary.maturedOn}. Payments are counted only up to maturity, because the schedule
              ends there, and the whole balance is due, so the unpaid principal is quoted below. To quote an extension
              instead, move the Maturity Date past the Good through Date and the balance drops off.
            </p>
          )}

          {reinstatementDate && summary?.maturityDate && summary.maturityDate !== summary.loanMaturityDate && (
            <p className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded p-3 mb-4">
              Quoted against a maturity date of {summary.maturityDate} rather than the {summary.loanMaturityDate || '-'}{' '}
              on the loan. This quote only: the maturity date on the loan is unchanged.
            </p>
          )}

          {reinstatementDate && summary?.paymentsPastDue === 0 && (
            <p className="text-sm text-gray-600 mb-4">
              No scheduled payment is past due as of this date. The quote below covers outstanding fees and charges
              only.
            </p>
          )}

          {reinstatementDate && (
            <div className="grid grid-cols-1 gap-2 mb-4">
              <div className="mb-3 flex justify-between items-center gap-3">
                <div>
                  <h3 className="font-semibold text-lg text-gray-800">Reinstatement Line Items</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Edit the values below as needed before generating the PDF.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {available.length > 0 && (
                    <select
                      className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-700"
                      value=""
                      onChange={(e) => {
                        if (e.target.value) addCategory(e.target.value)
                      }}
                    >
                      <option value="">+ Add category…</option>
                      {available.map((c) => (
                        <option key={c.title} value={c.title}>
                          {c.title.trim()}
                        </option>
                      ))}
                    </select>
                  )}
                  <Button link onClick={addCustomLine} color="shade-blue">
                    + Custom line
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                {lineItems.map((item, index) => {
                  const titleInput: InputType = {
                    inputType: 'text',
                    title: 'Line Item Description',
                    value: item.title || '',
                  }
                  const valueInput: InputType = {
                    inputType: 'text',
                    title: 'Amount',
                    value: item.value,
                    type: 'thousandSep',
                    prefix: '$',
                    disabled: item.optional ? !item.included : false,
                  }

                  return (
                    <div key={index} className="flex items-center gap-2">
                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <div>
                          <RenderInput
                            input={titleInput}
                            Key={`${index}_title`}
                            onChange={(_: string, value: any) => onLineItemTitleChange(index, value)}
                          />
                        </div>
                        <div>
                          <RenderInput
                            input={valueInput}
                            Key={`${index}_value`}
                            onChange={(_: string, value: any) => onLineItemChange(index, value)}
                          />
                        </div>
                      </div>
                      {/* Fixed-width slot so every row's inputs line up whether or not it has a checkbox. */}
                      <div className="w-20 shrink-0 flex items-center">
                        {item.optional && (
                          <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap cursor-pointer">
                            <input type="checkbox" checked={item.included} onChange={() => toggleIncluded(index)} />
                            Include
                          </label>
                        )}
                      </div>
                      <span
                        className="w-6 shrink-0 flex justify-center text-red-500 hover:cursor-pointer hover-shadow1 rounded"
                        onClick={() => removeLineItem(index)}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </span>
                    </div>
                  )
                })}

                {/* Total */}
                <div className="flex items-center gap-2 border-t border-gray-200 pt-2 mt-2">
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <div className="flex items-center font-semibold text-gray-800">{TOTAL_TITLE}</div>
                    <div className="flex items-center justify-end font-semibold text-gray-900 text-lg pr-1">
                      {formatUSD(total)}
                    </div>
                  </div>
                  {/* Match the line rows' trailing slots (checkbox + trash) so the total aligns. */}
                  <div className="w-20 shrink-0" />
                  <span className="w-6 shrink-0" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex justify-center items-center">
        <Button onClick={onGenerate} className="px-[36px]">
          {sendEmail ? 'Generate & Email' : 'Generate & Download'}
        </Button>
      </div>
    </div>
  )
}
